import {
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    Chip,
    IconButton,
} from "@mui/material";
import Icon from "../atoms/Icon";
import { Task } from "../../interface/returns";

interface TaskListProps {
    tasks: Task[];
    onTaskClick: (task: Task) => void;
    title: string;
}

const TaskList = ({ tasks, onTaskClick, title }: TaskListProps) => {
    const getStatusColor = (status: Task["status"]) => {
        switch (status) {
            case "pending":
                return "#ff9800";
            case "in_progress":
                return "#2196f3";
            case "completed":
                return "#4caf50";
            case "in_use":
                return "#ff3333";
            default:
                return "#9e9e9e";
        }
    };

    const getPriorityIcon = (priority?: Task["priority"]) => {
        switch (priority) {
            case "high":
                return "PriorityHigh";
            case "medium":
                return "Remove";
            case "low":
                return "ArrowDownward";
            default:
                return null;
        }
    };

    return (
        <Box
            sx={{
                width: "100%",
                bgcolor: "background.paper",
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflow: "hidden",
            }}
        >
            <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: 600, flexShrink: 0 }}
            >
                {title}
            </Typography>
            <List sx={{ width: "100%", flex: 1, overflowY: "auto" }}>
                {tasks.map((task) => (
                    <ListItem
                        key={task.id}
                        disablePadding
                        sx={{ mb: 1 }}
                        secondaryAction={
                            <IconButton
                                edge="end"
                                onClick={() => onTaskClick(task)}
                            >
                                <Icon name="ChevronRight" />
                            </IconButton>
                        }
                    >
                        <ListItemButton
                            onClick={() => onTaskClick(task)}
                            sx={{
                                px: 0,
                                borderRadius: 1,
                                "&:hover": {
                                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                                },
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 0, mr: 3 }}>
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        backgroundColor: getStatusColor(task.status),
                                    }}
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <>
                                        {task.title}
                                        {task.priority && (
                                            <Icon
                                                name={getPriorityIcon(task.priority) || ""}
                                                sx={{
                                                    fontSize: 16,
                                                    color:
                                                        task.priority === "high"
                                                            ? "#f44336"
                                                            : task.priority === "medium"
                                                              ? "#ff9800"
                                                              : "#4caf50",
                                                }}
                                            />
                                        )}
                                    </>
                                }
                                slotProps={{
                                    primary: {
                                        component: "div",
                                        sx: { display: "flex", alignItems: "center", gap: 1 },
                                    },
                                    secondary: {
                                        component: "div",
                                        sx: { display: "flex", flexDirection: "column", gap: 1, mt: 0.5 },
                                    },
                                }}
                                secondary={
                                    <>
                                        {task.fileName && (
                                            <Typography
                                                variant="subtitle2"
                                                color="text.primary"
                                                sx={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {task.fileName}
                                            </Typography>
                                        )}
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Chip
                                                label={task.status.replace("_", " ")}
                                                size="small"
                                                sx={{
                                                    backgroundColor: `${getStatusColor(task.status)}20`,
                                                    color: getStatusColor(task.status),
                                                    fontWeight: 500,
                                                    fontSize: "0.75rem",
                                                }}
                                            />
                                            {task.assignee && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Assigned to: {task.assignee}
                                                </Typography>
                                            )}
                                        </Box>
                                    </>
                                }
                            />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
        </Box>
    );
};

export default TaskList;
